/** @prettier */

/**
 * ユーザ編集ダイアログ
 */

import {
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Dialog,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  RadioGroup,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  NativeSelect,
  Box,
} from '@mui/material';
import {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {
  getCompositionsGroups,
  patchSettingSites,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {apiErrorAlert} from '../../functions/apiErrorAlert';
import {iconBlack} from '../../define';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
import ChangePayingUserAuthorityMessage from '../../component/ChangePayingUserAuthorityMessage';

type GroupAuthorityType = {
  'group-id': string;
  'authority': 'manager' | 'viewer' | 'none';
  'floor': string;
  'group-name': string;
};

const UserEditDialog = (props: {
  authorityIDInfo: apiIF.resParameters.userAuthority;
  setAuthInfoChange: React.Dispatch<React.SetStateAction<boolean>>;
  isOpenDialog: boolean;
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  //パスパラメータ取得
  const {siteID} = useParams();

  //編集前のユーザの情報を取得
  const tempAdmin = props.authorityIDInfo['site-authority'] === 'admin';
  let tempOther = 'none';
  // other機器（Qton,Hyozan,Chiller,HCCVQ）のうち１つでも権限を持っていた場合にother権限をviewerにする
  if (
    props.authorityIDInfo['qton-authority'] === 'viewer' ||
    props.authorityIDInfo['hyozan-authority'] === 'viewer' ||
    props.authorityIDInfo['hccvq-authority'] === 'viewer' ||
    props.authorityIDInfo['chiller-authority'] === 'viewer'
  ) {
    tempOther = 'viewer';
  }
  const tempQton = props.authorityIDInfo['qton-authority'];
  const tempHyozan = props.authorityIDInfo['hyozan-authority'];
  const tempModbus = props.authorityIDInfo['hccvq-authority'];
  const tempChiller = props.authorityIDInfo['chiller-authority'];

  const [adminCheckBox, setAdminCheckBox] = useState(tempAdmin); //Admin権限
  const [otherAuth, setOtherAuth] = useState(tempOther); //Other機器全体の権限
  const [qtonAuth, setQtonAuth] = useState(tempQton); //Qtonの権限
  const [hyozanAuth, setHyozanAuth] = useState(tempHyozan); //Hyozanの権限
  const [modbusAuth, setModbusAuth] = useState(tempModbus); //Modbusの権限
  const [chillerAuth, setChillerAuth] = useState(tempChiller); //Modbusの権限
  const [tempGroup, setTempGroup] = useState<GroupAuthorityType[]>([]); //グループの権限情報（編集前）
  const [groupAuthority, setGroupAuthority] = useState<GroupAuthorityType[]>(
    []
  ); //グループの権限情報
  const [tempBatch, setTempBatch] = useState<{[key: string]: string}>({}); //一括登録用権限情報（編集前）
  const [batchAuthority, setBatchAuthority] = useState<{[key: string]: string}>(
    {}
  ); //一括登録用権限情報
  const [errorCode, setErrorCode] = useState(''); //編集失敗時のcode格納用変数
  const [errorParam, setErrorParam] = useState(''); //編集失敗時のparameter格納用変数
  const [expanded, setExpanded] = useState<string[]>([]); //展開しているアコーディオンの位置
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [getAPIWait, setGetAPIWait] = useState(false); //グループ情報取得API応答待ちフラグ
  const [editAPIWait, setEditAPIWait] = useState(false); //権限修正API応答待ちフラグ

  const trans = useTranslation().t; //翻訳関数

  //グループの情報を取得
  useEffect(() => {
    //ダイアログを開いた時のみAPIを実行
    if (siteID === undefined || props.isOpenDialog === false) return;

    setGetAPIWait(true);
    const parameter: apiIF.reqParameters.getCompositionsGroups = {
      'fields': 'group-name,floor',
      'max-result': 400,
    };
    getCompositionsGroups(siteID, parameter)
      .then(response => {
        //グループごとの権限の初期値
        const tempGroup = props.authorityIDInfo['airconditioner-authority']
          .reduce((result, authInfo) => {
            //該当のグループの情報を取得
            const groupInfo = response['group-informations'].find(
              info => info['group-id'] === authInfo['group-id']
            );

            //グループの名前、フロア数を追記
            //見つからなかった場合は削除
            if (
              groupInfo === undefined ||
              groupInfo['group-name'] === undefined ||
              groupInfo['floor'] === undefined
            ) {
              return result;
            }
            result.push({
              ...authInfo,
              'group-name': groupInfo['group-name'],
              'floor': groupInfo['floor'],
            });
            return result;
          }, [] as GroupAuthorityType[])
          .sort((a, b) => {
            //フロア順に並べ替え
            return Number(b.floor) - Number(a.floor);
          })
          .sort((a, b) => {
            //フロアが同じ場合はグループ名順で並べ替え
            if (Number(a.floor) === Number(b.floor)) {
              return a['group-name'] < b['group-name'] ? -1 : 1;
            } else {
              return 0;
            }
          });
        setTempGroup(tempGroup);
        setGroupAuthority(tempGroup);

        //一括設定用の権限の初期値
        //空調機フロア毎設定の初期値をnopとする
        let currentFloor = '';
        let tempFloor: {[key: string]: string} = {};
        for (const authInfo of tempGroup) {
          if (authInfo['floor'] === currentFloor) continue;
          currentFloor = authInfo['floor'];
          tempFloor[currentFloor] = 'nop';
        }
        setTempBatch(tempFloor);
        setBatchAuthority(tempFloor);
      })
      .finally(() => {
        setGetAPIWait(false);
      });
  }, [siteID, props.isOpenDialog, props.authorityIDInfo]);

  /**
   * OKボタン押下時処理
   */
  const handleOK = () => {
    //パスパラメータを取得できない場合
    //サイトの権限の情報を取得できない場合
    if (siteID === undefined) {
      return;
    }

    setEditAPIWait(true);
    let editData: apiIF.reqParameters.patchSettingSites = {
      'user-id': props.authorityIDInfo['user-id'],
      'admin-authority': adminCheckBox ? 'active' : 'inactive',
      'qton-authority': qtonAuth,
      'hyozan-authority': hyozanAuth,
      'hccvq-authority': modbusAuth,
      'chiller-authority': chillerAuth,
    };
    const tempAirconditioner = groupAuthority.map(info => ({
      'group-id': info['group-id'],
      'authority': info['authority'],
    }));
    if (tempAirconditioner.length > 0) {
      editData['airconditioner-authority'] = tempAirconditioner;
    }
    //権限編集API
    patchSettingSites(siteID, editData)
      .then(response => {
        //トップ画面更新フラグtrue
        props.setAuthInfoChange(true);
        props.setIsOpenDialog(false);
      })
      .catch(err => {
        if (err.response.data.code === 'CHANGE_ADMIN_USER_FORBIDDEN') {
          setIsOpenErrorDialog(true);
          setErrorCode(err.response.data.code);
          setErrorParam(err.response.data.parameter);
        } else {
          apiErrorAlert(trans);
        }
      })
      .finally(() => {
        setEditAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
    setAdminCheckBox(tempAdmin);
    setGroupAuthority(tempGroup);
    setOtherAuth(tempOther);
    setQtonAuth(tempQton);
    setHyozanAuth(tempHyozan);
    setModbusAuth(tempModbus);
    setChillerAuth(tempChiller);
    setBatchAuthority(tempBatch);
  };

  /**
   * チェックボックス押下時処理
   * @param event
   * @returns
   */
  const handleAdmin = (event: React.ChangeEvent<HTMLInputElement>) => {
    //チェックボックスOFF
    if (event.target.checked === false) {
      setAdminCheckBox(false);
      return;
    }
    //チェックボックスON
    setAdminCheckBox(true);

    //Qton, Hyozan, CO2冷凍機の権限をViewer
    setOtherAuth('viewer');
    setQtonAuth('viewer');
    setHyozanAuth('viewer');
    setModbusAuth('viewer');
    setChillerAuth('viewer');

    //空調機グループ権限をmanager
    if (groupAuthority.length > 0) {
      const temp = groupAuthority.map(info => {
        const result = {...info};
        result.authority = 'manager';
        return result;
      });
      setGroupAuthority(temp);
    }

    //空調機のフロア毎の権限をmanager
    setBatchAuthority(
      Object.keys(batchAuthority).reduce(
        (result, floor) => {
          result[floor] = 'manager';
          return result;
        },
        {} as {
          [key: string]: string;
        }
      )
    );
  };

  /**
   * Other機器権限ラジオボタン変更時処理
   * @param event
   */
  const handleOther = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === 'Viewer') {
      setOtherAuth('viewer');
      setQtonAuth('viewer');
      setHyozanAuth('viewer');
      setModbusAuth('viewer');
      setChillerAuth('viewer');
    } else {
      setOtherAuth('none');
      setQtonAuth('none');
      setHyozanAuth('none');
      setModbusAuth('none');
      setChillerAuth('none');
    }
  };

  /**
   * 空調機フロア毎の権限変更時処理
   * @param event
   * @param floor 変更するフロア
   * @param index ダイアログでの通し番号
   */
  const handleFloorAll = (
    event: React.ChangeEvent<HTMLSelectElement>,
    floor: string
  ) => {
    if (event.target.value !== 'nop' && groupAuthority.length > 0) {
      const temp = groupAuthority.map(info => {
        const result = {...info};
        if (
          info['floor'] === floor &&
          (event.target.value === 'manager' ||
            event.target.value === 'viewer' ||
            event.target.value === 'none')
        ) {
          result['authority'] = event.target.value;
        }
        return result;
      });
      setGroupAuthority(temp);
    }

    //対象フロアのグループ一括設定の反映
    if (floor in batchAuthority) {
      setBatchAuthority({
        ...batchAuthority,
        [floor]: event.target.value as string,
      });
    }
  };
  /**
   * 空調機グループ毎の権限変更時処理
   * @param event
   * @param groupID ダイアログでの通し番号
   * @returns
   */
  const groupAuthChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    groupID: string
  ) => {
    if (groupAuthority.length === 0) {
      return;
    }

    if (
      event.target.value === 'manager' ||
      event.target.value === 'viewer' ||
      event.target.value === 'none'
    ) {
      const value = event.target.value;
      setGroupAuthority(
        groupAuthority.map(info => {
          if (info['group-id'] !== groupID) return info;
          return {
            'group-id': info['group-id'],
            'group-name': info['group-name'],
            'floor': info.floor,
            'authority': value,
          } as GroupAuthorityType;
        })
      );
    }
  };

  /**
   * アコーディオン開閉時の処理
   * @param floor 開閉したフロア
   * @returns
   */
  const handleExpanded = (floor: string) => {
    return (event: React.SyntheticEvent, newExpanded: boolean) => {
      if (newExpanded) {
        //展開するアコーディオンを追加
        setExpanded([...expanded, floor]);
      } else {
        setExpanded(expanded.filter(value => value !== floor));
      }
    };
  };

  /**
   * 空調機の権限用アコーディオンデータ
   * @param groupAuthority 空調機の権限
   * @returns アコーディオンデータ
   */
  const createAccordionData = (groupAuthority: GroupAuthorityType[]) => {
    let currentFloor = '';
    let accordionData: {
      floorNum: string;
      groupAuthority: {
        groupID: string;
        groupName: string;
        authority: string;
      }[];
    }[] = [];

    for (const authInfo of groupAuthority) {
      if (authInfo['floor'] === currentFloor) continue;
      currentFloor = authInfo['floor'];

      const temp = groupAuthority.filter(
        info => info.floor === authInfo['floor']
      );
      accordionData.push({
        floorNum: authInfo['floor'],
        groupAuthority: temp.map(info => ({
          groupID: info['group-id'],
          groupName: info['group-name'],
          authority: info['authority'],
        })),
      });
    }

    return accordionData;
  };

  /**
   * Other機器の権限を表示
   * @param props props
   * @returns JSX
   * - authority Other機器の権限
   * - isAdmin Admin権限かどうか
   * - email ユーザのメールアドレス
   */
  const OtherAuthority = (props: {
    authority: string;
    isAdmin: boolean;
    email: string;
  }) => {
    return (
      <Grid container alignItems="center">
        <Grid item xs={6}>
          <Typography>
            <Box component="span" className="siteSecurity__dialog--rt">
              {trans('UserEditDialog.dialogBox.item.value.other')}
            </Box>
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Grid container justifyContent={'center'}>
            <FormControl variant="standard">
              <RadioGroup
                row
                value={`${props.authority
                  .slice(0, 1)
                  .toUpperCase()}${props.authority.slice(1)}`}
                onChange={handleOther}>
                <Grid item xs={8}>
                  <FormControlLabel
                    disabled={props.isAdmin}
                    value="Viewer"
                    control={<Radio id={`${props.email}QtonViewerBtn`} />}
                    label={trans(
                      'UserEditDialog.dialogBox.item.value.securityLevel.viewer'
                    )}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControlLabel
                    disabled={props.isAdmin}
                    value="None"
                    control={<Radio id={`${props.email}QtonNoneBtn`} />}
                    label={trans(
                      'UserEditDialog.dialogBox.item.value.securityLevel.none'
                    )}
                  />
                </Grid>
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  /**
   * グループごとの権限を表示
   * @param props props
   * @returns JSX
   * - groupAuthority グループごとの権限
   * - floorAuthority フロアごとの権限
   * - isAdmin Admin権限かどうか
   * - email ユーザのメールアドレス
   */
  const AirconAuthority = (props: {
    groupAuthority: GroupAuthorityType[];
    floorAuthority: {
      [key: string]: string;
    };
    isAdmin: boolean;
    email: string;
  }) => {
    //一つもない場合は表示しない
    if (props.groupAuthority.length === 0) {
      return <></>;
    }

    const accordionData = createAccordionData(props.groupAuthority);
    return (
      <>
        <Typography>
          {trans('UserEditDialog.dialogBox.item.title.ac')}&nbsp;:
        </Typography>
        {accordionData.map(floorInfo => {
          return (
            <Accordion
              key={floorInfo.floorNum}
              expanded={expanded.includes(floorInfo.floorNum)}
              onChange={handleExpanded(floorInfo.floorNum)}>
              <AccordionSummary
                expandIcon={
                  <img
                    className="siteSecurity__dialog__expandIcon"
                    src={iconBlack.CHEVRON_DOWN.src}
                    alt={`${trans(
                      `UserEditDialog.dialogBox.item.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                    )}`}
                  />
                }
                aria-controls="panel1a-content"
                id="panel1a-header">
                <Typography>
                  {trans(
                    `UserEditDialog.dialogBox.item.value.ac.floor.floor${floorInfo.floorNum.replace(
                      '-',
                      'B'
                    )}`
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* フロア毎の権限設定 */}
                {trans('UserEditDialog.dialogBox.item.value.ac.selAll')}
                <FormControl variant="standard">
                  <NativeSelect
                    value={props.floorAuthority[floorInfo.floorNum]}
                    disabled={props.isAdmin}
                    onChange={e => handleFloorAll(e, floorInfo.floorNum)}
                    id={`${props.email}floor${floorInfo.floorNum}AuthorizationSelect`}
                    className="siteSecurity__dialog--airconAll">
                    <option value={'nop'}></option>
                    <option value={'manager'}>
                      {trans(
                        'UserEditDialog.dialogBox.item.value.securityLevel.manager'
                      )}
                    </option>
                    <option value={'viewer'}>
                      {trans(
                        'UserEditDialog.dialogBox.item.value.securityLevel.viewer'
                      )}
                    </option>
                    <option value={'none'}>
                      {trans(
                        'UserEditDialog.dialogBox.item.value.securityLevel.none'
                      )}
                    </option>
                  </NativeSelect>
                </FormControl>
                {/* グループ毎の権限設定 */}
                {floorInfo.groupAuthority.map(groupInfo => {
                  return (
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      key={groupInfo.groupID}>
                      <Grid
                        title={groupInfo.groupName}
                        item
                        xs={5}
                        className="siteSecurity__dialog--airconGroup">
                        {groupInfo.groupName}
                      </Grid>
                      <Grid item xs={7}>
                        <FormControl variant="standard" component="fieldset">
                          <RadioGroup
                            row
                            value={groupInfo.authority}
                            onChange={e =>
                              groupAuthChange(e, groupInfo.groupID)
                            }>
                            <FormControlLabel
                              disabled={props.isAdmin}
                              value="manager"
                              control={
                                <Radio
                                  id={`${props.email}${groupInfo.groupName}ManagerBtn`}
                                />
                              }
                              label={trans(
                                'UserEditDialog.dialogBox.item.value.securityLevel.manager'
                              )}
                            />
                            <FormControlLabel
                              disabled={props.isAdmin}
                              value="viewer"
                              control={
                                <Radio
                                  id={`${props.email}${groupInfo.groupName}ViewerBtn`}
                                />
                              }
                              label={trans(
                                'UserEditDialog.dialogBox.item.value.securityLevel.viewer'
                              )}
                            />
                            <FormControlLabel
                              disabled={props.isAdmin}
                              value="none"
                              control={
                                <Radio
                                  id={`${props.email}${groupInfo.groupName}NoneBtn`}
                                />
                              }
                              label={trans(
                                'UserEditDialog.dialogBox.item.value.securityLevel.none'
                              )}
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </Grid>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </>
    );
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        className="siteSecurity__dialog"
        maxWidth="sm"
        fullWidth={true}
        id="mv-form-edit-dialog-security">
        <DialogTitle id="form-dialog-title">
          {trans('UserEditDialog.dialogBox.title')}
        </DialogTitle>
        <DialogContent>
          {/* メールアドレス */}
          <Typography>
            {trans('UserEditDialog.dialogBox.item.title.email')}&nbsp;:
          </Typography>
          <Typography gutterBottom>
            <Box component="span" className="siteSecurity__dialog--email">
              {props.authorityIDInfo['e-mail']}
            </Box>
          </Typography>
          {/** グループの状態値を取得中は表示しない */}
          {getAPIWait ? (
            <Box>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Admin */}
              <Typography>
                {trans('UserEditDialog.dialogBox.item.title.adminPrivileges')}
                &nbsp;:
              </Typography>
              <FormGroup className="siteSecurity__dialog--admin">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={adminCheckBox}
                      onChange={handleAdmin}
                      id={`mv-CheckAdmin${props.authorityIDInfo[
                        'e-mail'
                      ].replace('@', '')}`}
                    />
                  }
                  label={trans(
                    'UserEditDialog.dialogBox.item.value.securityLevel.admin'
                  )}
                />
              </FormGroup>
              <Typography>
                {trans('UserEditDialog.dialogBox.item.title.other')}&nbsp;:
              </Typography>
              {/* Other */}
              <OtherAuthority
                authority={otherAuth}
                isAdmin={adminCheckBox}
                email={props.authorityIDInfo['e-mail']}
              />
              {/* 空調機 */}
              <AirconAuthority
                groupAuthority={groupAuthority}
                floorAuthority={batchAuthority}
                isAdmin={adminCheckBox}
                email={props.authorityIDInfo['e-mail']}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={editAPIWait}
            onClick={handleClose}
            buttonText={trans('UserEditDialog.dialogBox.buttonTxt.cancel')}
            id={`mv-btnCancelEditSecurity${props.authorityIDInfo[
              'e-mail'
            ].replace('@', '')}`}
          />
          <PrimaryButton
            onClick={handleOK}
            buttonText={trans('UserEditDialog.dialogBox.buttonTxt.ok')}
            id={`mv-btnOkEditSecurity${props.authorityIDInfo['e-mail'].replace(
              '@',
              ''
            )}`}
            disabled={editAPIWait}
            apiWait={editAPIWait}
          />
        </DialogActions>
      </Dialog>
      {errorCode === 'CHANGE_ADMIN_USER_FORBIDDEN' ? (
        <ChangePayingUserAuthorityMessage
          errorCode={errorCode}
          gwNameList={errorParam}
          open={isOpenErrorDialog}
          setOpen={setIsOpenErrorDialog}
          id={`${props.authorityIDInfo['e-mail'].replace(
            '@',
            ''
          )}ChangePayingUserAuthorityErrDlgBtn`}
        />
      ) : (
        <APIErrorMessage
          open={isOpenErrorDialog}
          setOpen={setIsOpenErrorDialog}
          id={`${props.authorityIDInfo['e-mail'].replace(
            '@',
            ''
          )}EditErrDlgBtn`}
        />
      )}
    </>
  );
};
export default UserEditDialog;
